// ---------------------------------------------------------------------------
// View-mode mutations for the Judge Factory page:
//
//   * useGraderDeletion — owns the delete confirmation flow for a judge:
//     fires the DELETE, and only on success removes it from the in-memory
//     list, advances selection, and closes the dialog. Mirrors the Agents
//     page: a loading state + inline error, and no optimistic removal.
// ---------------------------------------------------------------------------

import { useCallback, useState } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import {
  useGraderActions,
  useGraderState,
} from '../../context/GraderContext.js';
import { WorkflowApiService } from '../../services/workflowApi.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  EntityType,
  OperationType,
  PanelType,
} from '../../telemetry/userbi.js';

import type { UseGraderDeletionResult } from './useGraderMutations.interface.js';

export type { UseGraderDeletionResult } from './useGraderMutations.interface.js';

// ---------------------------------------------------------------------------
// useGraderDeletion
// ---------------------------------------------------------------------------

export function useGraderDeletion(
  workspaceName: string,
): UseGraderDeletionResult {
  const client = useHttpClient();
  const { trackOperation } = useUserBi();
  const { graders, selectedId, judgeToDelete } = useGraderState();
  const { selectGrader, closeDeleteDialog, removeGrader } = useGraderActions();

  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmDelete = useCallback(async () => {
    if (!judgeToDelete) return;
    const { id } = judgeToDelete;
    setDeleting(true);
    setError(null);
    try {
      await trackOperation(
        {
          panelType: PanelType.GradersPage,
          entityType: EntityType.Grader,
          operation: OperationType.Delete,
        },
        () => new WorkflowApiService(client).deleteGrader(workspaceName, id),
      );
      removeGrader(id);
      if (selectedId === id) {
        const remaining = graders.filter((j) => j.id !== id);
        selectGrader(remaining[0]?.id ?? null);
      }
      closeDeleteDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete judge.');
    } finally {
      setDeleting(false);
    }
  }, [
    judgeToDelete,
    client,
    workspaceName,
    removeGrader,
    selectedId,
    graders,
    selectGrader,
    closeDeleteDialog,
    trackOperation,
  ]);

  const cancelDelete = useCallback(() => {
    setError(null);
    closeDeleteDialog();
  }, [closeDeleteDialog]);

  return { deleting, error, confirmDelete, cancelDelete };
}
